import { z } from "zod";
import { request } from "./defs.ts";
import { projectIdSchema } from "./schemas.ts";
import { changeSetFileContentSchema } from "./changesets.ts";


/**
 * Git readiness of a Project on the connected Machine (decided by the Machine Agent, never by the browser).
 *  READY             git runs, the Project is a Git repository and HEAD is valid (usable by the Worktree flow)
 *  GIT_NOT_INSTALLED the git executable cannot be found / executed
 *  NOT_A_REPOSITORY  git exists, the Project is not a Git repository
 *  NO_HEAD           a repository without an initial commit
 *  ERROR             readiness cannot safely be determined (fail closed)
 */
export const GIT_READINESS_STATES = [
  "READY",
  "GIT_NOT_INSTALLED",
  "NOT_A_REPOSITORY",
  "NO_HEAD",
  "ERROR",
] as const;
export const gitReadinessStateSchema = z.enum(GIT_READINESS_STATES);
export type GitReadinessState = z.infer<typeof gitReadinessStateSchema>;

export const gitReadinessSchema = z.strictObject({
  state: gitReadinessStateSchema,
  headCommit: z
    .string()
    .regex(/^[0-9a-f]{7,40}$/i)
    .optional(),
  /** Short, user-safe explanation (never a stack trace). */
  message: z.string().max(300).optional(),
});
export type GitReadiness = z.infer<typeof gitReadinessSchema>;

export const gitInstallPlanSchema = z.strictObject({
  platform: z.enum(["darwin", "win32", "linux", "other"]),
  method: z.enum(["xcode-clt", "winget", "package-manager", "manual"]),
  /** True only when racler can start a native installer itself (never sudo). */
  canLaunch: z.boolean(),
  /** Exact command the user can run themselves (shown with a Copy action when racler cannot launch it). */
  command: z.string().max(200).optional(),
  instructions: z.string().max(500),
});
export type GitInstallPlan = z.infer<typeof gitInstallPlanSchema>;

export const gitInstallResultSchema = z.strictObject({
  launched: z.boolean(),
  plan: gitInstallPlanSchema,
  message: z.string().max(300),
});
export type GitInstallResult = z.infer<typeof gitInstallResultSchema>;

export const gitIdentitySchema = z.strictObject({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().min(3).max(200),
});
export type GitIdentity = z.infer<typeof gitIdentitySchema>;

export const gitPrepareResultSchema = z.strictObject({
  readiness: gitReadinessSchema,
  /** No Git identity is available: nothing was changed; ask for name/email and call again. */
  needsIdentity: z.boolean(),
});
export type GitPrepareResult = z.infer<typeof gitPrepareResultSchema>;

export const gitCommitResultSchema = z.strictObject({
  ok: z.boolean(),
  readiness: gitReadinessSchema,
  needsIdentity: z.boolean(),
  commitSha: z.string().optional(),
  error: z.string().optional(),
});
export type GitCommitResult = z.infer<typeof gitCommitResultSchema>;

export const gitDiscardResultSchema = z.strictObject({
  ok: z.boolean(),
  readiness: gitReadinessSchema,
  error: z.string().optional(),
});
export type GitDiscardResult = z.infer<typeof gitDiscardResultSchema>;

export const gitDiffResultSchema = z.strictObject({
  files: z.array(changeSetFileContentSchema).max(100),
});
export type GitDiffResult = z.infer<typeof gitDiffResultSchema>;

const empty = z.strictObject({});

export const gitRequests = {
  /** Read-only: never mutates the Project. */
  "system/git.readiness": request(
    "system",
    "git.readiness",
    z.strictObject({ projectId: projectIdSchema }),
    gitReadinessSchema,
  ),
  "system/git.installPlan": request("system", "git.installPlan", empty, gitInstallPlanSchema),
  /** Starts a native installer where supported. Never runs sudo or bypasses OS prompts. */
  "system/git.install": request("system", "git.install", empty, gitInstallResultSchema),
  /**
   * Initializes Git in the Project root (when needed) and creates the minimal local baseline commit. Local only:
   * no global config, no remote, no push. `confirmed` must be literally true: callers need an explicit user decision.
   */
  "system/git.prepare": request(
    "system",
    "git.prepare",
    z.strictObject({
      projectId: projectIdSchema,
      confirmed: z.literal(true),
      /** Used for this one baseline commit only; never written to any Git config. */
      identity: gitIdentitySchema.optional(),
    }),
    gitPrepareResultSchema,
  ),
  /**
   * Creates one checkpoint commit of current local changes before Autonomous. Local only:
   * no global config, no remote, no push.
   */
  "system/git.commit": request(
    "system",
    "git.commit",
    z.strictObject({
      projectId: projectIdSchema,
      message: z.string().min(1).max(500),
      /** Used for this one commit only; never written to any Git config. */
      identity: gitIdentitySchema.optional(),
    }),
    gitCommitResultSchema,
  ),
  /**
   * Discards uncommitted local changes in the Project root before Autonomous.
   * Restores tracked modified and deleted files to HEAD and removes untracked Project files.
   * Ignored files are preserved. Fails closed if repository root != project root.
   */
  "system/git.discard": request(
    "system",
    "git.discard",
    z.strictObject({ projectId: projectIdSchema }),
    gitDiscardResultSchema,
  ),
  /** Read-only diff of uncommitted local changes against HEAD commit. */
  "system/git.diff": request(
    "system",
    "git.diff",
    z.strictObject({ projectId: projectIdSchema }),
    gitDiffResultSchema,
  ),
} as const;
